import { BuiltInAttribute } from "@orsane/schema/tasks";
import { addDays, type Day, isAfter, isToday, startOfMonth, startOfWeek } from "date-fns";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import type { ColumnDef } from "../ColumnModel";
import type { Row } from "../RowModel";

function getCreatedAtGroupingValue(createdAt: Date, weekStartsOn: Day): string {
    const now = new Date();

    if (isToday(createdAt)) {
        return "Today";
    }

    // Check if it's in the last week (7 days ago)
    const lastWeekStart = startOfWeek(now, { weekStartsOn });
    if (isAfter(createdAt, lastWeekStart)) {
        return "This week";
    }

    // Check if it's in the last month
    const lastMonthStart = startOfMonth(now);
    if (isAfter(createdAt, lastMonthStart)) {
        return "This month";
    }

    // Check if it's in the previous month
    const prevMonthStart = startOfMonth(addDays(lastMonthStart, -1));
    if (isAfter(createdAt, prevMonthStart)) {
        return "Previous month";
    }

    return "Older";
}

function CreatedAtCell({ createdAt }: { createdAt: Date }) {
    const { formatDate } = useDateFormatter();
    return <div className="text-sm flex items-center px-3 h-full">{formatDate(createdAt)}</div>;
}

export function buildCreatedAtColumn(isVisible: boolean, weekStartsOn: Day): ColumnDef {
    return {
        id: BuiltInAttribute.createdAt,
        header: "Created",
        isVisible: isVisible,
        cssColDef: "130px",
        isRight: false,
        isReadOnly: true,
        getSortValue: ({ row }: { row: Row }) => {
            return getCreatedAtGroupingValue(row.data.createdAt, weekStartsOn);
        },
        getGroupHeader: ({ row }: { row: Row }) => {
            return getCreatedAtGroupingValue(row.data.createdAt, weekStartsOn);
        },
        cell: ({ row }: { row: Row }) => {
            return <CreatedAtCell createdAt={row.data.createdAt} />;
        },
    };
}
